<template>
  <div class="container-fluid p-3">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="h3 my-0 mx-0 text-foreground">Review Sequence</h3>
        <p class="text-muted-foreground text-sm">Default automated workflow for all review requests</p>
      </div>
        <div class="flex items-center space-x-4">
          <div class="text-right">
            <div class="text-sm text-muted-foreground">Active Contacts</div>
            <div class="text-2xl font-bold">{{ activeContacts }}</div>
          </div>
          <div class="text-right">
            <div class="text-sm text-muted-foreground">Completion Rate</div>
            <div class="text-2xl font-bold text-success">{{ completionRate }}%</div>
          </div>
          <button
            class="btn btn-outline-secondary round-xl px-4 save-changes-btn"
            :disabled="loadingSave || !hasChanges"
            @click="saveAutomation"
          >
            <b-spinner v-if="loadingSave" small class="mr-2"></b-spinner>
            Save Changes
          </button>
          <router-link
            v-if="user && user.business"
            :to="{
              name: 'request-reviews',
              params: {
                /* eslint-disable-next-line vue/camelcase */
                unique_id: user.business.unique_id + '-' + user.business.id,
              },
            }"
            class="btn bg-primary space-x-3 round-xl px-4 request-review-btn"
            target="_blank"
            ><i class="uil uil-star"></i
            ><span class="text-sm font-medium"> Request Review</span>
          </router-link>
        </div>
    </div>
    <hr class="pb-4 pt-0" style="margin: 0 -40px 0 -20px !important" />
      <div class="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
        <div class="max-w-2xl mx-auto space-y-4">
          <!-- Start Sequence Card -->
          <div class="relative">
            <div class="relative rounded-lg border-2 border-success bg-success/5 p-4 transition-all duration-200 hover:shadow-md">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="flex items-center space-x-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="grip-icon"
                    >
                      <circle cx="9" cy="12" r="1"></circle>
                      <circle cx="9" cy="5" r="1"></circle>
                      <circle cx="9" cy="19" r="1"></circle>
                      <circle cx="15" cy="12" r="1"></circle>
                      <circle cx="15" cy="5" r="1"></circle>
                      <circle cx="15" cy="19" r="1"></circle>
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="step-icon"
                    >
                      <polygon points="6 3 20 12 6 21 6 3"></polygon>
                    </svg>
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center space-x-3">
                      <div class="font-medium">Start Sequence</div>
                    </div>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <div class="step-badge step-badge-secondary">START</div>
                </div>
              </div>
            </div>
            <div class="flex justify-center py-2">
              <button
                class="add-step-button"
                @click="showAddBlockModal(-1)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="add-icon"
                >
                  <path d="M5 12h14"></path>
                  <path d="M12 5v14"></path>
                </svg>
              </button>
            </div>
            <div class="absolute left-1/2 top-full transform -translate-x-0.5 w-0.5 h-4 bg-border -mt-2 z-0"></div>
          </div>

          <!-- Sequence Blocks -->
          <div
            v-for="(block, index) in automation.blocks"
            :key="index"
            class="relative"
          >
            <div
              class="sequence-card"
              :class="getCardClass(block)"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="flex items-center space-x-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="grip-icon"
                    >
                      <circle cx="9" cy="12" r="1"></circle>
                      <circle cx="9" cy="5" r="1"></circle>
                      <circle cx="9" cy="19" r="1"></circle>
                      <circle cx="15" cy="12" r="1"></circle>
                      <circle cx="15" cy="5" r="1"></circle>
                      <circle cx="15" cy="19" r="1"></circle>
                    </svg>
                    <svg
                      v-if="getStepIcon(block)"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="step-icon"
                    >
                      <g v-html="getStepIcon(block)"></g>
                    </svg>
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center space-x-3">
                      <div class="font-medium">{{ getStepTitle(block) }}</div>
                      <div
                        v-if="getStepContacts(block)"
                        class="contacts-badge"
                      >
                        {{ getStepContacts(block) }} contacts
                      </div>
                    </div>
                    <div
                      v-if="getStepPreview(block)"
                      class="text-sm text-muted-foreground mt-1"
                    >
                      <span class="line-clamp-1">{{ getStepPreview(block) }}</span>
                    </div>
                    <div
                      v-if="getStepMetrics(block).length"
                      class="mt-3 p-3"
                    >
                      <div class="flex flex-wrap gap-3">
                        <div
                          v-for="(metric, mIndex) in getStepMetrics(block)"
                          :key="mIndex"
                          class="metric-badge"
                          :class="metric.class"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="metric-icon"
                          >
                            <g v-html="metric.icon"></g>
                          </svg>
                          <span class="metric-text">{{ metric.text }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <div class="step-badge" :class="getStepBadgeClass(block)">
                    {{ getStepType(block) }}
                  </div>
                  <div class="flex space-x-1">
                    <button
                      class="action-button"
                      @click="editBlock(block, index)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="action-icon"
                      >
                        <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"></path>
                      </svg>
                    </button>
                    <button
                      class="action-button action-button-danger"
                      @click="deleteBlock(index)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="action-icon"
                      >
                        <path d="M3 6h18"></path>
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                        <line x1="10" x2="10" y1="11" y2="17"></line>
                        <line x1="14" x2="14" y1="11" y2="17"></line>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex justify-center py-2">
              <button
                class="add-step-button"
                @click="showAddBlockModal(index)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="add-icon"
                >
                  <path d="M5 12h14"></path>
                  <path d="M12 5v14"></path>
                </svg>
              </button>
            </div>
            <div class="absolute left-1/2 top-full transform -translate-x-0.5 w-0.5 h-4 bg-border -mt-2 z-0"></div>
          </div>

          <!-- End Sequence Card -->
          <div class="relative">
            <div class="relative rounded-lg border-2 border-destructive bg-destructive/5 p-4 transition-all duration-200 hover:shadow-md">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="flex items-center space-x-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="grip-icon"
                    >
                      <circle cx="9" cy="12" r="1"></circle>
                      <circle cx="9" cy="5" r="1"></circle>
                      <circle cx="9" cy="19" r="1"></circle>
                      <circle cx="15" cy="12" r="1"></circle>
                      <circle cx="15" cy="5" r="1"></circle>
                      <circle cx="15" cy="19" r="1"></circle>
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="step-icon"
                    >
                      <rect x="14" y="4" width="4" height="16" rx="1"></rect>
                      <rect x="6" y="4" width="4" height="16" rx="1"></rect>
                    </svg>
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center space-x-3">
                      <div class="font-medium">Sequence Complete</div>
                      <div class="contacts-badge">{{ totalContacts }} contacts</div>
                    </div>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <div class="step-badge step-badge-secondary">END</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    <!-- Edit Email Modal -->
    <div
      v-if="isShowEditEmailModal"
      class="modal-overlay"
      @click.self="closeEditEmailModal"
    >
      <div
        role="dialog"
        class="edit-email-modal"
        @click.stop
      >
        <div class="flex flex-col space-y-1.5 text-center sm:text-left mb-4">
          <h2 class="text-lg font-semibold leading-none tracking-tight">Edit Email Step</h2>
        </div>
        <div class="space-y-4 flex-1 overflow-y-auto pr-2">
          <!-- Label -->
          <div>
            <label class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" for="step-label">Label</label>
            <input
              id="step-label"
              v-model="emailForm.label"
              class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm mt-1"
              placeholder="Step label"
            />
          </div>

          <!-- From Name -->
          <div>
            <label class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" for="from-name">From Name</label>
            <input
              id="from-name"
              v-model="emailForm.fromName"
              class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm mt-1"
              placeholder="Your Business Name"
            />
          </div>

          <!-- Subject -->
          <div>
            <label class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" for="subject">Subject</label>
            <div class="flex space-x-2 mt-1">
              <input
                id="subject"
                v-model="emailForm.subject"
                class="flex h-10 flex-1 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                placeholder="Email subject"
              />
              <div class="flex-shrink-0" style="width: 160px; min-width: 160px;">
                <SimplePersonalizationMenu
                  @selected="(field) => insertPersonalizationField(field, 'subject')"
                />
              </div>
            </div>
          </div>

          <!-- Body -->
          <div>
            <label class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" for="email-body">Body</label>
            <div class="space-y-2 mt-1">
              <div class="d-flex align-items-center gap-2 mb-2">
                <span class="text-sm text-muted-foreground">Insert:</span>
                <SimplePersonalizationMenu
                  size="small"
                  @selected="(field) => insertPersonalizationField(field, 'email-body')"
                />
              </div>
              <vue2-tinymce-editor
                :id="emailBodyEditorId"
                ref="emailBodyEditor"
                v-model="emailForm.body"
                :options="tinymceOptions"
                :height="280"
                @editorInit="onEmailBodyEditorInit"
              />
            </div>
          </div>

          <!-- Divider -->
          <div class="shrink-0 bg-border h-[1px] w-full"></div>

          <!-- Actions -->
          <div class="flex justify-end space-x-2">
            <button
              class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border-none bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
              @click="closeEditEmailModal"
            >
              Cancel
            </button>
            <button
              class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border-none bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
              @click="saveEmailBlock"
            >
              Save
            </button>
          </div>
        </div>
        <button
          type="button"
          class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none border-none bg-transparent"
          @click="closeEditEmailModal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="h-4 w-4"
          >
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
          <span class="sr-only">Close</span>
        </button>
      </div>
    </div>

    <!-- Edit SMS Modal -->
    <div
      v-if="isShowEditSMSModal"
      class="modal-overlay"
      @click.self="closeEditSMSModal"
    >
      <div
        role="dialog"
        class="edit-email-modal"
        @click.stop
      >
        <div class="flex flex-col space-y-1.5 text-center sm:text-left mb-4">
          <h2 class="text-lg font-semibold leading-none tracking-tight">Edit SMS Step</h2>
        </div>
        <div class="space-y-4 flex-1 overflow-y-auto pr-2">
          <!-- Label -->
          <div>
            <label class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" for="sms-step-label">Label</label>
            <input
              id="sms-step-label"
              v-model="smsForm.label"
              class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm mt-1"
              placeholder="Step label"
            />
          </div>

          <!-- Content -->
          <div>
            <label class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" for="sms-content">Content</label>
            <div class="space-y-2 mt-1">
              <div style="width: 160px;">
                <SimplePersonalizationMenu
                  @selected="(field) => insertPersonalizationField(field, 'sms-content')"
                />
              </div>
              <textarea
                id="sms-content"
                v-model="smsForm.content"
                class="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                rows="4"
                maxlength="160"
                placeholder="SMS content..."
              ></textarea>
              <p class="text-xs text-muted-foreground text-right">{{ smsForm.content.length }} / 160</p>
            </div>
          </div>

          <!-- Divider -->
          <div class="shrink-0 bg-border h-[1px] w-full"></div>

          <!-- Actions -->
          <div class="flex justify-end space-x-2">
            <button
              class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border-none bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
              @click="closeEditSMSModal"
            >
              Cancel
            </button>
            <button
              class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border-none bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
              @click="saveSMSBlock"
            >
              Save
            </button>
          </div>
        </div>
        <button
          type="button"
          class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none border-none bg-transparent"
          @click="closeEditSMSModal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="h-4 w-4"
          >
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
          <span class="sr-only">Close</span>
        </button>
      </div>
    </div>

    <!-- Edit Wait Modal -->
    <div
      v-if="isShowEditWaitModal"
      class="modal-overlay"
      @click.self="closeEditWaitModal"
    >
      <div
        role="dialog"
        class="edit-email-modal"
        @click.stop
      >
        <div class="flex flex-col space-y-1.5 text-center sm:text-left mb-4">
          <h2 class="text-lg font-semibold leading-none tracking-tight">Edit Wait Step</h2>
        </div>
        <div class="space-y-4 flex-1 overflow-y-auto pr-2">
          <div>
            <label class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" for="wait-step-label">Label</label>
            <input
              id="wait-step-label"
              v-model="waitForm.label"
              class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm mt-1"
              placeholder="Step label"
            />
          </div>

          <div>
            <label class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Wait duration</label>
            <div class="flex space-x-2 mt-1">
              <select
                v-model.number="waitForm.duration"
                class="flex h-10 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option
                  v-for="n in waitDurationOptions"
                  :key="n"
                  :value="n"
                >
                  {{ n }}
                </option>
              </select>
              <select
                v-model="waitForm.period"
                class="flex h-10 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="seconds">seconds</option>
                <option value="minutes">minutes</option>
                <option value="hour(s)">hour(s)</option>
                <option value="day(s)">day(s)</option>
                <option value="week(s)">week(s)</option>
                <option value="month(s)">month(s)</option>
                <option value="year(s)">year(s)</option>
              </select>
            </div>
          </div>

          <div class="shrink-0 bg-border h-[1px] w-full"></div>

          <div class="flex justify-end space-x-2">
            <button
              class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border-none bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
              @click="closeEditWaitModal"
            >
              Cancel
            </button>
            <button
              class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border-none bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
              @click="saveWaitBlock"
            >
              Save
            </button>
          </div>
        </div>
        <button
          type="button"
          class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none border-none bg-transparent"
          @click="closeEditWaitModal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="h-4 w-4"
          >
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
          <span class="sr-only">Close</span>
        </button>
      </div>
    </div>

    <!-- Add Block Modal -->
    <div
      v-if="isShowAddBlockModal"
      class="modal-overlay"
      @click.self="isShowAddBlockModal = false"
    >
      <div
        role="dialog"
        class="add-block-modal"
        @click.stop
      >
        <div class="modal-header">
          <h2 class="modal-title">Add a New Action</h2>
          <button
            type="button"
            class="modal-close-button"
            @click="isShowAddBlockModal = false"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="close-icon"
            >
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </button>
        </div>
        <div class="modal-content">
          <div class="action-options">
            <div
              class="action-option"
              @click="selectActionType('SMS')"
            >
              <div class="action-option-icon action-option-sms">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="action-icon-large"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
              </div>
              <div class="action-option-content">
                <div class="action-option-name">Send SMS</div>
                <p class="action-option-description">Send an SMS message</p>
              </div>
            </div>

            <div
              class="action-option"
              @click="selectActionType('Email')"
            >
              <div class="action-option-icon action-option-email">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="action-icon-large"
                >
                  <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                </svg>
              </div>
              <div class="action-option-content">
                <div class="action-option-name">Send Email</div>
                <p class="action-option-description">Send an email</p>
              </div>
            </div>

            <div
              class="action-option"
              @click="selectActionType('Wait')"
            >
              <div class="action-option-icon action-option-wait">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="action-icon-large"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <div class="action-option-content">
                <div class="action-option-name">Wait</div>
                <p class="action-option-description">Wait for a certain period of time, or until conditions are matched</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { Vue2TinymceEditor } from 'vue2-tinymce-editor'
import SimplePersonalizationMenu from './SimplePersonalizationMenu.vue'
import '../../../design/custom/components/_dashboard.scss'

export default {
  components: {
    SimplePersonalizationMenu,
    Vue2TinymceEditor,
  },
  data() {
    return {
      automation: {
        blocks: [],
        active: true,
      },
      loadingSave: false,
      loadingStats: false,
      constAutomation: null,
      isShowAddBlockModal: false,
      addBlockIndex: -1,
      isShowEditEmailModal: false,
      isShowEditSMSModal: false,
      isShowEditWaitModal: false,
      editingBlock: null,
      editingBlockIndex: -1,
      emailForm: {
        label: '',
        fromName: '',
        subject: '',
        body: '',
      },
      smsForm: {
        label: '',
        content: '',
      },
      waitForm: {
        label: '',
        duration: 1,
        period: 'day(s)',
      },
      stats: {
        total_contacts: 0,
        sent_contacts: 0,
        finished_contacts: 0,
        completion_rate: 0,
      },
      stepStats: {}, // Store statistics for each step/block
      emailBodyEditorId: 'rs-email-body-' + Date.now(),
      emailTinyEditor: null,
      tinymceOptions: {
        menubar: false,
        toolbar: 'fontsizeselect formatselect | bold italic underline strikethrough forecolor backcolor | alignleft aligncenter alignright alignjustify | numlist bullist outdent indent | link removeformat code',
        forced_root_block: false,
        content_style: 'body { font-family: inherit; line-height: 1.5; }',
      },
    }
  },
  computed: {
    user() {
      return this.$store.getters['auth/user']
    },
    activeContacts() {
      // Active contacts = sent contacts (contacts that have started the sequence)
      return this.stats.sent_contacts || 0
    },
    completionRate() {
      // Completion rate from API
      return this.stats.completion_rate || 0
    },
    totalContacts() {
      // Total contacts from API
      return this.stats.total_contacts || 0
    },
    hasChanges() {
      if (!this.constAutomation) return false
      return JSON.stringify(this.automation) !== JSON.stringify(this.constAutomation)
    },
    waitDurationOptions() {
      return Array.from({ length: 60 }, (_, i) => i + 1)
    },
  },
  mounted() {
    if (this.user && this.user.business) {
      if (
        this.user.business.hasOwnProperty('review_request_series') &&
        this.user.business.review_request_series
      ) {
        this.automation = JSON.parse(JSON.stringify(this.user.business.review_request_series))
      }
      this.constAutomation = JSON.parse(JSON.stringify(this.automation))
      this.fetchContactRequestStats()
    }
  },
  methods: {
    async fetchContactRequestStats() {
      if (!this.user || !this.user.business) {
        return
      }

      this.loadingStats = true
      try {
        const response = await axios.get(
          `/businesses/${this.user.business.id}/contact-request-stats`
        )

        if (response.data.success && response.data.data) {
          const data = response.data.data
          this.stats = {
            total_contacts: data.total_contacts || 0,
            sent_contacts: data.sent_contacts || 0,
            finished_contacts: data.finished_contacts || 0,
            completion_rate: data.completion_rate || 0,
          }
        }
        
        // Fetch step-level statistics
        await this.fetchStepStats()
      } catch (error) {
        console.error('Error fetching contact request stats:', error)
        // Don't show error to user, just use default values
      } finally {
        this.loadingStats = false
      }
    },
    async fetchStepStats() {
      if (!this.user || !this.user.business) {
        return
      }

      try {
        // First, fetch contact requests to get visits data
        const contactsResponse = await axios.post(
          `/businesses/${this.user.business.id}/get-contacts`,
          {
            params: { per_page: 9999, is_bulk: true },
            business_id: this.user.business.id,
            agency_id: this.user.business.agency_id || null,
          }
        )

        const stepStatsMap = {}
        const contacts = contactsResponse.data && contactsResponse.data.data 
          ? (Array.isArray(contactsResponse.data.data) ? contactsResponse.data.data : [])
          : []

        // Process visits for clicks
        contacts.forEach((contact) => {
          if (contact.visits && Array.isArray(contact.visits)) {
            contact.visits.forEach((visit) => {
              // Use current_step to determine which step this visit belongs to
              const step = visit.step || contact.current_step || 0
              if (step > 0) {
                if (!stepStatsMap[step]) {
                  stepStatsMap[step] = {
                    email: { sent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0, failed: 0 },
                    sms: { sent: 0, delivered: 0, clicked: 0, failed: 0 },
                    contacts: new Set(),
                  }
                }
                
                const visitType = (visit.type || '').toLowerCase()
                if (visitType === 'click' || visitType === 'email') {
                  stepStatsMap[step].email.clicked++
                } else if (visitType === 'sms' || visitType === 'text') {
                  stepStatsMap[step].sms.clicked++
                }
                
                if (contact.id) {
                  stepStatsMap[step].contacts.add(contact.id)
                }
              }
            })
          }
        })

        // Use contact requests' current_step and automation blocks to infer sent counts
        // Match each step to the block type (Email, SMS, or Wait)
        contacts.forEach((contact) => {
          const currentStep = contact.current_step || 0
          if (currentStep > 0 && this.automation.blocks && this.automation.blocks.length > 0) {
            // For each step up to current_step, we know at least one message was sent
            for (let step = 1; step <= currentStep && step <= this.automation.blocks.length; step++) {
              const blockIndex = step - 1
              const block = this.automation.blocks[blockIndex]
              
              if (!block) continue
              
              if (!stepStatsMap[step]) {
                stepStatsMap[step] = {
                  email: { sent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0, failed: 0 },
                  sms: { sent: 0, delivered: 0, clicked: 0, failed: 0 },
                  contacts: new Set(),
                }
              }
              
              // Determine message type from block action
              const isEmail = block.action === 'SendEmail' || 
                             (block.action === 'Notification' && block.options && block.options.method === 'Email')
              const isSMS = block.action === 'Notification' && block.options && block.options.method === 'Sms'
              
              // Skip Wait blocks
              if (block.action === 'Wait' || block.action === 'WaitConditional') {
                if (contact.id) {
                  stepStatsMap[step].contacts.add(contact.id)
                }
                continue
              }
              
              // Count sent messages based on contact's available channels
              if (isEmail && contact.email) {
                stepStatsMap[step].email.sent++
                // Assume delivered if contact progressed past this step
                if (currentStep > step) {
                  stepStatsMap[step].email.delivered++
                } else if (currentStep === step) {
                  // Currently at this step, assume delivered (otherwise wouldn't be here)
                  stepStatsMap[step].email.delivered++
                }
              }
              
              if (isSMS && contact.cell_phone) {
                stepStatsMap[step].sms.sent++
                // Assume delivered if contact progressed past this step
                if (currentStep > step) {
                  stepStatsMap[step].sms.delivered++
                } else if (currentStep === step) {
                  // Currently at this step, assume delivered
                  stepStatsMap[step].sms.delivered++
                }
              }
              
              if (contact.id) {
                stepStatsMap[step].contacts.add(contact.id)
              }
            }
          }
        })

        // Convert Sets to counts and store
        Object.keys(stepStatsMap).forEach((step) => {
          stepStatsMap[step].contacts = stepStatsMap[step].contacts.size
        })

        this.stepStats = stepStatsMap
      } catch (error) {
        console.error('Error fetching step stats:', error)
        // Don't show error, just use empty stats
        this.stepStats = {}
      }
    },
    getCardClass(block) {
      const action = block.action
      if (action === 'SendEmail' || action === 'Notification') {
        if (block.options && block.options.method === 'Email') {
          return 'sequence-card-primary'
        }
        if (block.options && block.options.method === 'Sms') {
          return 'sequence-card-warning'
        }
      }
      if (action === 'Wait' || action === 'WaitConditional') {
        return 'sequence-card-muted'
      }
      return 'sequence-card-muted'
    },
    getStepIcon(block) {
      const action = block.action
      if (action === 'SendEmail' || (action === 'Notification' && block.options && block.options.method === 'Email')) {
        return '<rect width="20" height="16" x="2" y="4" rx="2"></rect><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>'
      }
      if (action === 'Notification' && block.options && block.options.method === 'Sms') {
        return '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>'
      }
      if (action === 'Wait' || action === 'WaitConditional') {
        return '<circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline>'
      }
      return ''
    },
    getStepTitle(block) {
      return block.name || block.action || 'Step'
    },
    getStepPreview(block) {
      if (block.action === 'SendEmail' && block.options && block.options.subject) {
        return `Subject: ${block.options.subject}`
      }
      if (block.action === 'Notification' && block.options && block.options.content) {
        return block.options.content.substring(0, 50) + '...'
      }
      if (block.action === 'Wait' && block.options) {
        if (block.options.action_type === 2) {
          return 'Conditional wait'
        }
        const duration = block.options.duration ?? block.options.days ?? 0
        const period = block.options.period || 'day(s)'
        return `${duration} ${period}`
      }
      if (block.action === 'WaitConditional') {
        return 'Conditional wait'
      }
      return ''
    },
    getStepContacts(block) {
      // Get contacts count for this step from stepStats
      // We need to match the block index to the step number
      const blockIndex = this.automation.blocks.indexOf(block)
      if (blockIndex === -1) return null
      
      // Step numbers are 1-indexed in the database (step 1, 2, 3...)
      const stepNumber = blockIndex + 1
      const stats = this.stepStats[stepNumber]
      
      if (stats && stats.contacts) {
        return stats.contacts
      }
      
      // For Wait blocks, return null (no contacts metric)
      if (block.action === 'Wait' || block.action === 'WaitConditional') {
        return null
      }
      
      return null
    },
    getStepMetrics(block) {
      const metrics = []
      
      // Get step number from block index (1-indexed)
      const blockIndex = this.automation.blocks.indexOf(block)
      if (blockIndex === -1) return metrics
      
      const stepNumber = blockIndex + 1
      const stats = this.stepStats[stepNumber]
      
      if (!stats) return metrics
      
      // SMS metrics
      if (block.action === 'Notification' && block.options && block.options.method === 'Sms') {
        const smsStats = stats.sms
        if (smsStats.sent > 0) {
          metrics.push({
            class: 'metric-blue',
            icon: '<path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"></path><path d="m21.854 2.147-10.94 10.939"></path>',
            text: `${smsStats.sent} sent`,
          })
        }
        if (smsStats.delivered > 0) {
          metrics.push({
            class: 'metric-green',
            icon: '<path d="M21.801 10A10 10 0 1 1 17 3.335"></path><path d="m9 11 3 3L22 4"></path>',
            text: `${smsStats.delivered} delivered`,
          })
        }
        if (smsStats.failed > 0) {
          metrics.push({
            class: 'metric-red',
            icon: '<circle cx="12" cy="12" r="10"></circle><path d="m15 9-6 6"></path><path d="m9 9 6 6"></path>',
            text: `${smsStats.failed} failed`,
          })
        }
        if (smsStats.clicked > 0) {
          const clickRate = smsStats.sent > 0 ? Math.round((smsStats.clicked / smsStats.sent) * 100) : 0
          metrics.push({
            class: 'metric-purple',
            icon: '<path d="M15 3h6v6"></path><path d="M10 14 21 3"></path><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>',
            text: `${smsStats.clicked} clicks (${clickRate}%)`,
          })
        }
      }
      
      // Email metrics
      if (block.action === 'SendEmail' || (block.action === 'Notification' && block.options && block.options.method === 'Email')) {
        const emailStats = stats.email
        if (emailStats.sent > 0) {
          metrics.push({
            class: 'metric-blue',
            icon: '<path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"></path><path d="m21.854 2.147-10.94 10.939"></path>',
            text: `${emailStats.sent} sent`,
          })
        }
        if (emailStats.delivered > 0) {
          metrics.push({
            class: 'metric-green',
            icon: '<path d="M21.801 10A10 10 0 1 1 17 3.335"></path><path d="m9 11 3 3L22 4"></path>',
            text: `${emailStats.delivered} delivered`,
          })
        }
        if (emailStats.opened > 0) {
          const openRate = emailStats.delivered > 0 ? Math.round((emailStats.opened / emailStats.delivered) * 100) : 0
          metrics.push({
            class: 'metric-orange',
            icon: '<path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path><circle cx="12" cy="12" r="3"></circle>',
            text: `${emailStats.opened} opened (${openRate}%)`,
          })
        }
        if (emailStats.clicked > 0) {
          const clickRate = emailStats.delivered > 0 ? Math.round((emailStats.clicked / emailStats.delivered) * 100) : 0
          metrics.push({
            class: 'metric-purple',
            icon: '<path d="M15 3h6v6"></path><path d="M10 14 21 3"></path><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>',
            text: `${emailStats.clicked} clicks (${clickRate}%)`,
          })
        }
        if (emailStats.bounced > 0) {
          metrics.push({
            class: 'metric-red',
            icon: '<circle cx="12" cy="12" r="10"></circle><path d="m15 9-6 6"></path><path d="m9 9 6 6"></path>',
            text: `${emailStats.bounced} bounced`,
          })
        }
        if (emailStats.failed > 0) {
          metrics.push({
            class: 'metric-red',
            icon: '<circle cx="12" cy="12" r="10"></circle><path d="m15 9-6 6"></path><path d="m9 9 6 6"></path>',
            text: `${emailStats.failed} failed`,
          })
        }
      }
      
      return metrics
    },
    getStepType(block) {
      if (block.action === 'SendEmail' || (block.action === 'Notification' && block.options && block.options.method === 'Email')) {
        return 'EMAIL'
      }
      if (block.action === 'Notification' && block.options && block.options.method === 'Sms') {
        return 'SMS'
      }
      if (block.action === 'Wait' || block.action === 'WaitConditional') {
        return 'WAIT'
      }
      return 'STEP'
    },
    getStepBadgeClass(block) {
      return 'step-badge-secondary'
    },
    showAddBlockModal(index) {
      this.addBlockIndex = index
      this.isShowAddBlockModal = true
    },
    selectActionType(actionType) {
      let newBlock = null

      if (actionType === 'SMS') {
        newBlock = {
          icon: 'uil uil-envelope',
          name: 'Send SMS',
          level: '1',
          action: 'Notification',
          options: {
            type: 'EmailAndSms',
            email: null,
            phone: null,
            method: 'Sms',
            source: 'Redemption',
            content: 'Hi [first_name], thanks for visiting [business_name]. Would you take few seconds and leave us a review - [review_link]',
            prequalify: false,
          },
          isNewBlock: true,
          description: 'Send an sms',
        }
      } else if (actionType === 'Email') {
        newBlock = {
          icon: 'uil uil-envelope',
          name: 'Send Email',
          level: '1',
          action: 'SendEmail',
          options: {
            name: '[business_name]',
            images: [],
            method: 'Email',
            message: '<p>Hi [first_name]</p><p>Thanks for using [business_name]. Would you take a few seconds and leave us a review - [review_link].</p><p>Sincerely,<br />[business_name]</p>',
            subject: 'Thank you for using [business_name]',
            integration_user: null,
          },
          isNewBlock: true,
          description: 'Send an email',
        }
      } else if (actionType === 'Wait') {
        newBlock = {
          icon: 'uil uil-clock',
          name: 'Wait',
          level: '1',
          action: 'Wait',
          options: {
            key: 'wait_for',
            duration: 1,
            period: 'day(s)',
            timeout: true,
            action_type: 1,
          },
          segments: {
            logic: 'And',
            data: [],
          },
          isNewBlock: true,
          description: 'Wait for a certain period of time, or until conditions are matched.',
        }
      }

      if (newBlock) {
        let insertedIndex = -1
        if (this.addBlockIndex === -1) {
          this.automation.blocks = [newBlock, ...this.automation.blocks]
          insertedIndex = 0
        } else if (this.addBlockIndex < this.automation.blocks.length - 1) {
          this.automation.blocks = [
            ...this.automation.blocks.slice(0, this.addBlockIndex + 1),
            newBlock,
            ...this.automation.blocks.slice(this.addBlockIndex + 1, this.automation.blocks.length),
          ]
          insertedIndex = this.addBlockIndex + 1
        } else {
          this.automation.blocks.push(newBlock)
          insertedIndex = this.automation.blocks.length - 1
        }

        if (actionType === 'Wait' && insertedIndex >= 0) {
          this.$nextTick(() => {
            this.editBlock(this.automation.blocks[insertedIndex], insertedIndex)
          })
        }
      }

      this.isShowAddBlockModal = false
      this.addBlockIndex = -1
    },
    editBlock(block, index) {
      // Check if this is an email block
      const isEmail = block.action === 'SendEmail' || 
                     (block.action === 'Notification' && block.options && block.options.method === 'Email')
      
      // Check if this is an SMS block
      const isSMS = block.action === 'Notification' && block.options && block.options.method === 'Sms'
      
      if (isEmail) {
        this.editingBlock = block
        this.editingBlockIndex = index
        this.emailBodyEditorId = 'rs-email-body-' + Date.now()
        // Populate form with existing values
        this.emailForm = {
          label: block.name || 'Send Email',
          fromName: block.options?.name || '[business_name]',
          subject: block.options?.subject || '',
          body: block.options?.message || '',
        }
        this.isShowEditEmailModal = true
      } else if (isSMS) {
        this.editingBlock = block
        this.editingBlockIndex = index
        // Populate form with existing values
        this.smsForm = {
          label: block.name || 'Send SMS',
          content: block.options?.content || '',
        }
        this.isShowEditSMSModal = true
      } else if (block.action === 'Wait' || block.action === 'WaitConditional') {
        this.editingBlock = block
        this.editingBlockIndex = index
        const options = block.options || {}
        this.waitForm = {
          label: block.name || 'Wait',
          duration: options.duration ?? options.days ?? 1,
          period: options.period || 'day(s)',
        }
        this.isShowEditWaitModal = true
      }
    },
    closeEditEmailModal() {
      this.isShowEditEmailModal = false
      this.editingBlock = null
      this.editingBlockIndex = -1
      this.emailTinyEditor = null
      this.emailForm = {
        label: '',
        fromName: '',
        subject: '',
        body: '',
      }
    },
    onEmailBodyEditorInit(editor) {
      this.emailTinyEditor = editor
    },
    closeEditSMSModal() {
      this.isShowEditSMSModal = false
      this.editingBlock = null
      this.editingBlockIndex = -1
      this.smsForm = {
        label: '',
        content: '',
      }
    },
    closeEditWaitModal() {
      this.isShowEditWaitModal = false
      this.editingBlock = null
      this.editingBlockIndex = -1
      this.waitForm = {
        label: '',
        duration: 1,
        period: 'day(s)',
      }
    },
    saveEmailBlock() {
      if (!this.editingBlock || this.editingBlockIndex === -1) {
        return
      }

      // Update the block with form values
      this.editingBlock.name = this.emailForm.label
      if (!this.editingBlock.options) {
        this.editingBlock.options = {}
      }
      this.editingBlock.options.name = this.emailForm.fromName
      this.editingBlock.options.subject = this.emailForm.subject
      this.editingBlock.options.message = this.emailForm.body

      // Update the automation blocks array
      this.automation.blocks[this.editingBlockIndex] = this.editingBlock

      // Close modal
      this.closeEditEmailModal()
    },
    saveSMSBlock() {
      if (!this.editingBlock || this.editingBlockIndex === -1) {
        return
      }

      // Update the block with form values
      this.editingBlock.name = this.smsForm.label
      if (!this.editingBlock.options) {
        this.editingBlock.options = {}
      }
      this.editingBlock.options.content = this.smsForm.content

      // Update the automation blocks array
      this.automation.blocks[this.editingBlockIndex] = this.editingBlock

      // Close modal
      this.closeEditSMSModal()
    },
    saveWaitBlock() {
      if (!this.editingBlock || this.editingBlockIndex === -1) {
        return
      }

      const duration = Math.max(1, parseInt(this.waitForm.duration, 10) || 1)

      this.editingBlock.name = this.waitForm.label
      if (!this.editingBlock.options) {
        this.editingBlock.options = {}
      }
      this.editingBlock.options.key = this.editingBlock.options.key || 'wait_for'
      this.editingBlock.options.duration = duration
      this.editingBlock.options.period = this.waitForm.period
      this.editingBlock.options.timeout = true
      if (!this.editingBlock.options.action_type) {
        this.editingBlock.options.action_type = 1
      }

      this.automation.blocks[this.editingBlockIndex] = this.editingBlock
      this.closeEditWaitModal()
    },
    insertPersonalizationField(field, target) {
      if (target === 'email-body' && this.emailTinyEditor) {
        this.emailTinyEditor.selection.setContent(field)
        this.emailForm.body = this.emailTinyEditor.getContent()
        return
      }

      this.$nextTick(() => {
        const element = document.getElementById(target)
        if (!element) return

        const start = element.selectionStart || 0
        const end = element.selectionEnd || 0
        const text = element.value || ''
        const newText = text.substring(0, start) + field + text.substring(end)

        if (target === 'subject') {
          this.emailForm.subject = newText
        } else if (target === 'sms-content') {
          this.smsForm.content = newText
        }

        this.$nextTick(() => {
          const newPosition = start + field.length
          element.setSelectionRange(newPosition, newPosition)
          element.focus()
        })
      })
    },
    deleteBlock(index) {
      if (confirm('Are you sure you want to delete this step?')) {
        this.automation.blocks.splice(index, 1)
      }
    },
    saveAutomation() {
      if (!this.user || !this.user.business) {
        console.error('User or business not available')
        return
      }

      this.loadingSave = true
      const params = {
        review_request_series: this.automation,
        business_id: this.user.business.id,
      }
      this.$store
        .dispatch('business/updateContactSeries', params)
        .then((resp) => {
          this.loadingSave = false
          // Handle response structure: resp.data.data contains BusinessResource
          // Handle response structure: resp.data.data contains BusinessResource
          let updatedSeries = null
          if (resp.data && resp.data.data && resp.data.data.review_request_series) {
            updatedSeries = resp.data.data.review_request_series
          } else if (resp.data && resp.data.review_request_series) {
            // Fallback for different response structure
            updatedSeries = resp.data.review_request_series
          }
          
          if (updatedSeries) {
            this.automation = JSON.parse(JSON.stringify(updatedSeries))
            this.constAutomation = JSON.parse(JSON.stringify(updatedSeries))
            // Update user's business in store to keep it in sync
            if (this.$store.state.auth.user && this.$store.state.auth.user.business) {
              this.$store.state.auth.user.business.review_request_series = this.automation
            }
          }
          // Refresh stats after saving
          this.fetchContactRequestStats()
          this.$bvToast.toast('Sequence saved successfully', {
            title: 'Success',
            variant: 'success',
            solid: true,
          })
        })
        .catch((err) => {
          if (err.response && err.response.data && err.response.data.errors) {
            console.log(err.response.data.errors)
          }
          this.loadingSave = false
          this.$bvToast.toast('Error saving sequence', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          })
        })
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasChanges) {
      const answer = window.confirm('Are you sure you want to leave without saving?')
      if (answer) {
        next()
      } else {
        next(false)
      }
    } else {
      next()
    }
  },
}
</script>

<style lang="scss" scoped>
.container-fluid {
  min-height: 100vh;
  background: white;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.justify-between {
  justify-content: space-between;
}

.space-x-4 {
  > * + * {
    margin-left: 1rem;
  }
}

.space-x-3 {
  > * + * {
    margin-left: 0.75rem;
  }
}

.space-x-2 {
  > * + * {
    margin-left: 0.5rem;
  }
}

.space-x-1 {
  > * + * {
    margin-left: 0.25rem;
  }
}

.text-right {
  text-align: right;
}

.text-sm {
  font-size: 0.875rem;
}

.text-2xl {
  font-size: 1.5rem;
  line-height: 2rem;
}

.font-bold {
  font-weight: 700;
}

.text-muted-foreground {
  color: #6b7280;
}

.text-success {
  color: #10b981;
}

.text-foreground {
  color: #111827;
}

.mt-1 {
  margin-top: 0.25rem;
}

.mt-3 {
  margin-top: 0.75rem;
}


.px-8 {
  padding-left: 2rem;
  padding-right: 2rem;
}

.py-2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.-mx-6 {
  margin-left: -1.5rem;
  margin-right: -1.5rem;
}

.mb-6 {
  margin-bottom: 1.5rem;
}

.border-b {
  border-bottom: 1px solid #e5e7eb;
}

.border-border {
  border-color: #e5e7eb;
}

.bg-background {
  background-color: white;
}

.rounded-lg {
  border-radius: 0.5rem;
}

.border {
  border: 1px solid #e5e7eb;
}

.bg-card {
  background-color: white;
}

.text-card-foreground {
  color: #111827;
}

.shadow-sm {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.p-6 {
  padding: 1.5rem;
}

.max-w-2xl {
  max-width: 42rem;
}

.mx-auto {
  margin-left: auto;
  margin-right: auto;
}

.space-y-4 {
  > * + * {
    margin-top: 1rem;
  }
}

.relative {
  position: relative;
}

// Sequence Cards
.sequence-card {
  border-radius: 0.5rem;
  border: 2px solid;
  padding: 1rem;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
}

.sequence-card-success {
  border-color: #10b981;
  background-color: rgba(16, 185, 129, 0.05);
}

.sequence-card-warning {
  border-color: #f59e0b;
  background-color: rgba(245, 158, 11, 0.05);
}

.sequence-card-primary {
  border-color: #3b82f6;
  background-color: rgba(59, 130, 246, 0.05);
}

.sequence-card-muted {
  border-color: #9ca3af;
  background-color: rgba(156, 163, 175, 0.05);
}

.sequence-card-destructive {
  border-color: #ef4444;
  background-color: rgba(239, 68, 68, 0.05);
}

.grip-icon {
  width: 1rem;
  height: 1rem;
  color: #6b7280;
  cursor: grab;
}

.step-icon {
  width: 1.25rem;
  height: 1.25rem;
}

.flex-1 {
  flex: 1 1 0%;
}

.font-medium {
  font-weight: 500;
}

.line-clamp-1 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  line-clamp: 1;
}

.contacts-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  border: 1px solid rgba(59, 130, 246, 0.3);
  padding: 0.125rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.step-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  border: 1px solid transparent;
  padding: 0.125rem 0.625rem;
  font-weight: 600;
  font-size: 0.75rem;
  transition: all 0.2s;
}

.step-badge-secondary {
  background-color: #f3f4f6;
  color: #6b7280;

  &:hover {
    background-color: #e5e7eb;
  }
}

.action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  font-size: 0.875rem;
  font-weight: 500;
  height: 2rem;
  width: 2rem;
  border-radius: 0.375rem;
  padding: 0;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #f9fafb;
    color: #374151;
  }
}

.action-button-danger {
  color: #ef4444;

  &:hover {
    background-color: #fee2e2;
    color: #dc2626;
  }
}

.action-icon {
  width: 1rem;
  height: 1rem;
}

.add-step-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 9999px;
  height: 2rem;
  width: 2rem;
  padding: 0;
  border: 2px dashed rgba(156, 163, 175, 0.3);
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #3b82f6;
    background-color: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
  }
}

.add-icon {
  width: 1rem;
  height: 1rem;
}

.absolute {
  position: absolute;
}

.left-1\/2 {
  left: 50%;
}

.top-full {
  top: 100%;
}

.transform {
  transform: translate(-50%, 0);
}

.w-0\.5 {
  width: 0.125rem;
}

.h-4 {
  height: 1rem;
}

.bg-border {
  background-color: #e5e7eb;
}

.-mt-2 {
  margin-top: -0.5rem;
}

.z-0 {
  z-index: 0;
}

// Metrics
.metric-badge {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-weight: 500;
}

.metric-blue {
  background-color: rgba(59, 130, 246, 0.1);
  color: #1e40af;
}

.metric-green {
  background-color: rgba(16, 185, 129, 0.1);
  color: #047857;
}

.metric-red {
  background-color: rgba(239, 68, 68, 0.1);
  color: #991b1b;
}

.metric-purple {
  background-color: rgba(147, 51, 234, 0.1);
  color: #6b21a8;
}

.metric-orange {
  background-color: rgba(249, 115, 22, 0.1);
  color: #9a3412;
}

.metric-icon {
  width: 0.75rem;
  height: 0.75rem;
}

.metric-text {
  font-weight: 500;
}

// Buttons
.save-changes-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  height: 2.5rem;
  padding: 0 1rem;
  border: 1px solid #e5e7eb;
  background: white;
  color: #374151;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #f9fafb;
    border-color: #d1d5db;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.bg-success\/5 {
  background-color: rgba(16, 185, 129, 0.05);
}

.bg-warning\/5 {
  background-color: rgba(245, 158, 11, 0.05);
}

.bg-primary\/5 {
  background-color: rgba(59, 130, 246, 0.05);
}

.bg-muted\/5 {
  background-color: rgba(156, 163, 175, 0.05);
}

.bg-destructive\/5 {
  background-color: rgba(239, 68, 68, 0.05);
}

.border-2 {
  border-width: 2px !important;
  border-style: solid !important;
}

.border-success {
  border-color: #10b981 !important;
}

.border-warning {
  border-color: #f59e0b;
}

.border-primary {
  border-color: #3b82f6;
}

.border-muted {
  border-color: #9ca3af;
}

.border-destructive {
  border-color: #ef4444 !important;
}

.bg-muted\/30 {
  background-color: rgba(156, 163, 175, 0.3);
}

.border-muted\/50 {
  border-color: rgba(156, 163, 175, 0.5);
}

.gap-3 {
  gap: 0.75rem;
}

.flex-wrap {
  flex-wrap: wrap;
}

// Add Block Modal
.modal-overlay {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 50;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.add-block-modal {
  position: relative;
  display: grid;
  width: 100%;
  max-width: 32rem;
  gap: 1rem;
  border: 1px solid #e5e7eb;
  background: white;
  padding: 0;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border-radius: 0.5rem;
  overflow: hidden;
}

.edit-email-modal {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 42rem;
  border: 1px solid #e5e7eb;
  background: white;
  padding: 1.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border-radius: 0.5rem;
  max-height: 90vh;
  overflow: hidden;
}

.edit-email-modal .space-y-4 {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding-right: 0.5rem;
  margin-right: -0.5rem;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

.edit-email-modal .space-y-4:hover {
  scrollbar-color: #888 #f1f1f1;
}

.edit-email-modal .space-y-4::-webkit-scrollbar {
  width: 6px;
}

.edit-email-modal .space-y-4::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 3px;
}

.edit-email-modal .space-y-4:hover::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.edit-email-modal .space-y-4::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 3px;
}

.edit-email-modal .space-y-4:hover::-webkit-scrollbar-thumb {
  background: #888;
}

.edit-email-modal .space-y-4:hover::-webkit-scrollbar-thumb:hover {
  background: #555;
}

// Personalization menu styles
.simple-personalization-menu .personalization-item:hover {
  background-color: #f9fafb;
}

// Ensure personalization menu is visible
.edit-email-modal .simple-personalization-menu {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

// WYSIWYG editor styles
.wysiwyg-toolbar {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  margin-bottom: 0.25rem;
}

.wysiwyg-select {
  border-radius: 0.25rem;
  border: 1px solid #d1d5db;
  background-color: white;
  padding: 0.125rem 0.5rem;
  font-size: 0.75rem;
  color: #374151;
  outline: none;
}

.wysiwyg-btn {
  border: none;
  outline: none;
  background-color: white;
  border-radius: 0.25rem;
  padding: 0.125rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    background-color: #e5e7eb;
  }
}

.wysiwyg-separator {
  width: 1px;
  height: 1.25rem;
  background-color: #d1d5db;
}

.wysiwyg-editor {
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.wysiwyg-editor:empty:before {
  content: attr(placeholder);
  color: #9ca3af;
}

.edit-email-modal .simple-personalization-menu {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.edit-email-modal .simple-personalization-menu button {
  display: flex !important;
  visibility: visible !important;
  opacity: 1 !important;
  background-color: #ffffff !important;
  border: 1px solid #e5e7eb !important;
  min-height: 32px !important;
  width: 100% !important;
  align-items: center !important;
  justify-content: space-between !important;
}

.edit-email-modal .simple-personalization-menu button span {
  display: inline-block !important;
  visibility: visible !important;
}

.edit-email-modal .simple-personalization-menu button svg,
.edit-email-modal .simple-personalization-menu svg {
  display: block !important;
  visibility: visible !important;
  opacity: 0.5 !important;
  width: 16px !important;
  height: 16px !important;
  flex-shrink: 0 !important;
  margin-left: auto !important;
}

// Remove borders from all buttons in modals (except personalization menu)
.edit-email-modal button:not(.simple-personalization-menu button),
.edit-email-modal button[type="button"]:not(.simple-personalization-menu button),
.edit-email-modal button[type="submit"]:not(.simple-personalization-menu button) {
  border: none !important;
  border-width: 0 !important;
  outline: none !important;
  box-shadow: none !important;
}

// Target all button elements more aggressively
.edit-email-modal * button {
  border: none !important;
  border-width: 0 !important;
}

.modal-header {
  padding: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.025em;
  margin: 0;
}

.modal-close-button {
  position: absolute;
  right: 1rem;
  top: 1rem;
  border-radius: 0.125rem;
  opacity: 0.7;
  background: transparent;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 1;
    background-color: #f3f4f6;
  }
}

.close-icon {
  width: 1rem;
  height: 1rem;
}

.modal-content {
  padding: 1.5rem;
}

.action-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.action-option {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #f9fafb;
    border-color: #3b82f6;
  }
}

.action-option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 0.5rem;
  flex-shrink: 0;
}

.action-option-sms {
  background-color: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.action-option-email {
  background-color: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.action-option-wait {
  background-color: rgba(156, 163, 175, 0.1);
  color: #6b7280;
}

.action-icon-large {
  width: 1.5rem;
  height: 1.5rem;
}

.action-option-content {
  flex: 1;
}

.action-option-name {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: #111827;
}

.action-option-description {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}
</style>

